// OrganizationsTable.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, TextField, TablePagination, TableSortLabel,
  Box, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OrganizationsDashboard from './OrganizationsDashboard';

const OrganizationsTable = ({ organizations, onRowClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [planNames, setPlanNames] = useState({});


  useEffect(() => {
    const fetchPlanNames = async () => {
      const fetched = {};

      const uniquePlanIds = [...new Set(organizations.map(org => org.plan_id).filter(Boolean))];
      const token = localStorage.getItem('access_token');

      await Promise.all(
        uniquePlanIds.map(async (planId) => {
          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan?plan_id=${planId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (!response.ok) throw new Error(`Failed for ${planId}`);
            const data = await response.json();
            fetched[planId] = data.plan_name || 'Unknown';
          } catch (err) {
            fetched[planId] = 'Error';
          }
        })
      );

      setPlanNames(fetched);
    };

    if (organizations.length) {
      fetchPlanNames();
    }
  }, [organizations]);

  // Search filtering
  const filteredOrganizations = useMemo(() => {
    if (!searchTerm) return organizations;

    return organizations.filter(org =>
      Object.values(org).some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [organizations, searchTerm]);

  // Sorting function
  const sortedOrganizations = useMemo(() => {
    const comparator = (a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle date sorting
      if (orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting (case insensitive)
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    };

    return [...filteredOrganizations].sort(comparator);
  }, [filteredOrganizations, orderBy, order]);

  // Paginated data
  const paginatedOrganizations = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedOrganizations.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedOrganizations, page, rowsPerPage]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'address', label: 'Address' },
    { key: 'contact_email', label: 'Contact Email' },
    { key: 'plan', label: 'Plan' }, 
    { key: 'phone', label: 'Phone' },
    { key: 'created_at', label: 'Created At' }
  ];

  return (
    <>
      <OrganizationsDashboard organizations={organizations} />
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Organizations List ({filteredOrganizations.length})
          </Typography>

          <TextField
            // fullWidth
            variant="outlined"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  <TableSortLabel
                    active={orderBy === column.key}
                    direction={orderBy === column.key ? order : 'asc'}
                    onClick={() => handleRequestSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrganizations.map((org) => (
              <TableRow
                key={org.organization_id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onRowClick(org.organization_id)}
              >
                <TableCell>{org.name}</TableCell>
                <TableCell>{org.address}</TableCell>
                <TableCell>{org.contact_email}</TableCell>
                <TableCell>{org.plan_id ? (planNames[org.plan_id] || '...') : 'None'}</TableCell>
                <TableCell>{org.phone}</TableCell>
                <TableCell>{new Date(org.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {paginatedOrganizations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? 'No organizations found matching your search.' : 'No organizations available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredOrganizations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </>
  );
};

export default OrganizationsTable;